import './CalculationPage.css';

import React, { JSX, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { Routes } from '../../../app/Routes';
import {
  BlechconCustomerConfigurationFrontend,
  BlechconOrder,
  BlechconUserInfo,
  clearOrderCalculations,
} from '../../../blechcon';
import { Button } from '../../../components/Button/Button';
import { FileTableRowComponent } from '../../../components/FileTableRowComponent/FileTableRowComponent';
import { GrindingWarningsInfo } from '../../../components/GrindingWarningsInfo/GrindingWarningsInfo';
import { Modal } from '../../../components/Modal';
import { PartDetails } from '../../../components/PartDetails/PartDetails';
import { persistAnalysisModels } from '../../../components/PartViewer/persistAnalysisModels';
import { localized } from '../../../config/localization';
import { calculateFiles } from '../../../services/wicam/calculateFile';

type Props = {
  readonly order: BlechconOrder;
  readonly userInfo: BlechconUserInfo;
  readonly persistPending: boolean;
  readonly isAdmin: boolean;
  readonly persistOrder: (order: Partial<BlechconOrder>) => Promise<BlechconOrder | null>;
  readonly configuration: BlechconCustomerConfigurationFrontend;
};

export function CalculationPage({
  order,
  userInfo,
  persistPending,
  isAdmin,
  persistOrder,
  configuration,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const [showBackModal, setShowBackModal] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    const materialTotalPrice = order?.prices?.materialTotalPrice ?? 0;

    if (materialTotalPrice <= 0) {
      calculateFiles(abortController.signal, order, configuration)
        .then(({ files, nestingFiles }) => {
          return persistOrder({
            id: order.id,
            files,
            nestingFiles,
          });
        })
        .catch((error) => {
          if (abortController.signal.aborted) {
            return;
          }
          console.error('error in calculation', error);
          toast.error(`error in file`);
        })
        .finally(() => {
          abortController.abort();
        });
    }
    return () => {
      abortController.abort();
    };
  }, [configuration, order, persistOrder]);

  async function handleNext() {
    const updatedOrder = await persistOrder({ id: order.id, checkoutStep: 4 });
    if (updatedOrder) {
      navigate(Routes.checkoutOrder(order.id));
    }
  }

  return (
    <>
      {showBackModal && (
        <Modal
          title={localized.BACK_TO_PARAMETERS}
          onCancel={{
            label: localized.ABORT,
            execute: () => {
              setShowBackModal(false);
            },
          }}
          onSuccess={{
            label: localized.BACK_TO_PARAMETERS,
            execute: async () => {
              await persistOrder({ ...clearOrderCalculations(order), checkoutStep: 2 });
              navigate(Routes.checkoutParameters(order.id));
            },
          }}
        >
          <p className="calculation-page-modal__label">{localized.BACK_TO_PARAMETERS_TEXT}</p>
        </Modal>
      )}

      <div className="calculation-page">
        <table className="table">
          <thead>
            <tr>
              <td className="table-cell__product">{localized.PRODUCT}</td>
              <td>{localized.QUANTITY}</td>
              <td>{localized.UNIT_PRICE} EUR</td>
              <td className="table-cell--right">{localized.TOTAL} EUR</td>
            </tr>
          </thead>
          <tbody className="calculation-page__table-body no-hover">
            {order.files.map((file, index) => (
              <FileTableRowComponent
                key={index}
                file={file}
                index={index}
                disabled={persistPending || !order?.prices?.materialTotalPrice}
                adminOnly={isAdmin}
                onAnalysisResult={async (analysisModels) => {
                  await persistAnalysisModels(order, file, analysisModels, persistOrder);
                }}
                configuration={configuration}
              >
                {({ part }) => {
                  return isAdmin ? <PartDetails part={part}></PartDetails> : null;
                }}
              </FileTableRowComponent>
            ))}
          </tbody>
        </table>

        <GrindingWarningsInfo files={order.files} />

        <div className="calculation-page__actions">
          <Button
            onClick={() => {
              setShowBackModal(true);
            }}
            secondary
          >
            {localized.BACK_TO_PARAMETERS}
          </Button>
          <Button
            disabled={
              persistPending ||
              (order.prices.materialTotalPrice || 0) === 0 ||
              order.userId !== userInfo.userId ||
              order.files.flatMap((f) => f.parts).some((p) => p.errorCode)
            }
            dataCy="continueButton"
            onClick={handleNext}
          >
            {localized.CONTINUE_TO_OFFER}
          </Button>
        </div>
      </div>
    </>
  );
}
